import { customElement, html, DeesElement, property, state, cssManager, css, type TemplateResult } from '@design.estate/dees-element';
import { demoFunc } from './dees-simple-appdash.demo.js';
import { themeDefaultStyles } from '../../00theme.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import '../../00group-button/dees-button/dees-button.js';
import '../../00group-overlay/dees-speechbubble/dees-speechbubble.js';
import type { DeesWorkspaceTerminal } from '../../00group-workspace/dees-workspace-terminal/dees-workspace-terminal.js';
import type { IExecutionEnvironment } from '../../00group-runtime/index.js';

declare global { interface HTMLElementTagNameMap { 'dees-simple-appdash': DeesSimpleAppDash; } }

export interface IView {
  name: string;
  iconName?: string;
  element?: DeesElement['constructor']['prototype'];
  subViews?: IView[];
}
export type TGlobalMessageType = 'info' | 'success' | 'warning' | 'error';
export interface IGlobalMessageAction { name: string; iconName?: string; action: () => void | Promise<void>; }
export interface IGlobalMessage {
  id: string;
  type: TGlobalMessageType;
  message: string;
  dismissible?: boolean;
  icon?: string;
  actions?: IGlobalMessageAction[];
}

/** A compact app shell. Views, notices and the terminal share one bounded workspace. */
@customElement('dees-simple-appdash')
export class DeesSimpleAppDash extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Simple'];
  @property() accessor name = 'Application Dashboard';
  @property({ type: Array }) accessor viewTabs: IView[] = [];
  @property() accessor terminalSetupCommand = 'echo "Terminal ready"';
  /** Consumer-owned runtime used by the embedded terminal. */
  @property({ type: Object }) accessor executionEnvironment: IExecutionEnvironment | null = null;
  @property({ type: Array }) accessor globalMessages: IGlobalMessage[] = [];
  /** Collapse to an icon rail. Narrow containers use the rail automatically. */
  @property({ type: Boolean }) accessor collapsed = false;
  @state() accessor selectedView!: IView;
  @state() accessor _activeMessages: IGlobalMessage[] = [];
  @state() private accessor compact = false;
  @state() private accessor terminalOpen = false;
  public currentTerminal: DeesWorkspaceTerminal | null = null;
  private currentView?: DeesElement;
  private terminalRequest = 0;

  constructor() {
    super();
    this.registerStartupFunction(async signal => {
      await this.updateComplete;
      if (signal.aborted) return;
      const observer = new ResizeObserver(entries => { this.compact = entries[0].contentRect.width < 640; });
      observer.observe(this);
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    });
  }

  public static styles = [themeDefaultStyles, cssManager.defaultStyles, css`
    :host { display: block; position: relative; width: 100%; height: 100%; overflow: hidden; color: var(--dees-color-text-primary); font: 13px/1.5 var(--dees-font-family); }
    * { box-sizing: border-box; }
    .maincontainer { position: absolute; inset: 0; display: grid; grid-template-columns: 224px minmax(0, 1fr); grid-template-rows: minmax(0, 1fr) 32px; background: var(--dees-color-bg-canvas); }
    .maincontainer.collapsed { grid-template-columns: 64px minmax(0, 1fr); }
    .appbar { display: grid; grid-template-rows: 64px minmax(0, 1fr) auto; min-height: 0; background: var(--dees-color-bg-secondary); border-right: 1px solid var(--dees-color-border-subtle); }
    .sidebar-header { display: flex; align-items: center; gap: 10px; padding: 0 16px; min-width: 0; }
    .appName { font-size: 15px; font-weight: 600; letter-spacing: -.02em; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .sidebar-header > dees-icon { flex-shrink: 0; }
    button { font: inherit; color: inherit; border: 0; background: transparent; cursor: pointer; }
    button:focus-visible { outline: 2px solid var(--dees-color-focus-ring); outline-offset: -2px; }
    .collapseToggle { margin-left: auto; flex-shrink: 0; width: 28px; height: 32px; display: grid; place-items: center; padding: 0; border-radius: var(--dees-radius-md); color: var(--dees-color-text-secondary); }
    .viewTabs-container { overflow: auto; min-height: 0; padding: 12px 8px; scrollbar-width: thin; }
    .section-label { padding: 0 12px 8px; color: var(--dees-color-text-secondary); font-size: 11px; font-weight: 500; }
    .viewTabs { display: grid; gap: 3px; }
    .viewTab, .action { display: flex; width: 100%; min-height: 38px; align-items: center; gap: 10px; padding: 8px 12px; text-align: left; border-radius: var(--dees-radius-lg); color: var(--dees-color-text-secondary); transition: background 140ms var(--dees-ease-standard), color 140ms var(--dees-ease-standard); }
    .viewTab dees-icon, .action dees-icon { flex-shrink: 0; }
    .viewTab span, .action span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .viewTab .chevron { margin-left: auto; transform: rotate(-90deg); transition: transform 180ms var(--dees-ease-standard); }
    .viewTab.groupActive .chevron, .viewTab.selected .chevron { transform: rotate(0); }
    .viewTab.selected { background: var(--dees-color-nav-selected); color: var(--dees-color-accent-primary); font-weight: 600; }
    .viewTab.groupActive { color: var(--dees-color-text-primary); }
    .subViews { display: grid; grid-template-rows: 0fr; margin: 0 0 0 20px; border-left: 1px solid var(--dees-color-border-subtle); transition: grid-template-rows 180ms var(--dees-ease-standard), margin 180ms var(--dees-ease-standard); }
    .subViews.expanded { grid-template-rows: 1fr; margin-top: 4px; margin-bottom: 8px; }
    .subViews-inner { display: grid; gap: 3px; min-height: 0; overflow: hidden; padding-left: 8px; opacity: 0; transform: translateY(-4px); transition: opacity 140ms var(--dees-ease-standard), transform 180ms var(--dees-ease-standard); }
    .subViews.expanded .subViews-inner { opacity: 1; transform: translateY(0); }
    .viewTab.sub { min-height: 34px; font-size: 12px; }
    .appActions { padding: 8px; border-top: 1px solid var(--dees-color-border-subtle); }
    .workspace { display: grid; grid-template-rows: 64px auto minmax(0, 1fr); min-width: 0; min-height: 0; }
    .workspaceHeader { display: flex; align-items: center; gap: 8px; padding: 0 24px; border-bottom: 1px solid var(--dees-color-border-subtle); min-width: 0; font-weight: 600; }
    .workspaceHeader span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .workspaceHeader .parent { color: var(--dees-color-text-secondary); font-weight: 400; }
    .contentArea { min-height: 0; min-width: 0; position: relative; display: grid; }
    .appcontent { overflow: auto; min-height: 0; min-width: 0; scrollbar-gutter: stable; overscroll-behavior: contain; }
    .appcontent[inert] { visibility: hidden; }
    .terminalLayer { position: absolute; inset: 0; }
    .terminalLayer[hidden] { display: none; }
    .terminalLayer > dees-workspace-terminal { display: block; position: absolute; inset: 0; width: 100%; height: 100%; }
    .messageBannerArea { max-height: 192px; overflow: auto; }
    .messageBanner { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; padding: 12px 24px; border-bottom: 1px solid var(--dees-color-border-subtle); background: var(--dees-color-bg-primary); }
    .messageIcon { display: grid; place-items: center; width: 28px; height: 28px; flex-shrink: 0; border-radius: var(--dees-radius-md); background: var(--message-bg); color: var(--message-fg); }
    .messageBanner-info { --message-bg: var(--dees-color-nav-selected); --message-fg: var(--dees-color-link); }
    .messageBanner-success { --message-bg: var(--dees-color-badge-success-bg); --message-fg: var(--dees-color-badge-success-fg); }
    .messageBanner-warning { --message-bg: var(--dees-color-badge-warning-bg); --message-fg: var(--dees-color-badge-warning-fg); }
    .messageBanner-error { --message-bg: var(--dees-color-badge-error-bg); --message-fg: var(--dees-color-badge-error-fg); }
    .messageBanner-text { flex: 1 1 160px; min-width: 0; overflow-wrap: anywhere; }
    .messageBanner-actions { display: flex; flex-wrap: wrap; gap: 8px; }
    .messageBanner-dismiss { display: grid; place-items: center; width: 28px; height: 28px; padding: 0; border-radius: var(--dees-radius-md); color: var(--dees-color-text-secondary); }
    .controlbar { grid-column: 1 / -1; display: flex; align-items: center; justify-content: flex-end; border-top: 1px solid var(--dees-color-border-subtle); background: var(--dees-color-bg-secondary); font-size: 11px; color: var(--dees-color-text-secondary); }
    .control { display: flex; align-items: center; gap: 6px; padding: 0 12px; height: 100%; }
    .status-terminal { border-left: 1px solid var(--dees-color-border-subtle); }
    /* The on face is the catalog's toggle-on face: the shared selected surface plus the accent
       *text* colour, not --dees-color-accent-primary, which only reaches 3.6:1 as a label. The
       hovered rule exists because the shared button:hover rule below outranks a plain
       [aria-pressed] rule and would wash the on state away under the pointer. */
    .status-terminal[aria-pressed='true'] { color: var(--dees-color-link); background: var(--dees-color-nav-selected); }
    .status-terminal[aria-pressed='true']:hover { color: var(--dees-color-link); background: color-mix(in srgb, var(--dees-color-nav-selected) 88%, var(--dees-color-text-primary)); }
    .collapsed .sidebar-header { justify-content: center; padding: 0; }
    .collapsed .sidebar-header > dees-icon, .collapsed .appName, .collapsed .section-label, .collapsed .viewTab span, .collapsed .viewTab .chevron, .collapsed .action span { display: none; }
    .collapsed .collapseToggle { margin: 0; width: 40px; height: 38px; }
    .collapsed .viewTab, .collapsed .action { justify-content: center; padding: 8px 0; }
    .collapsed .subViews { margin-left: 0; padding: 0; border: 0; }
    .collapsed .subViews-inner { padding-left: 0; }
    .collapsed .sub { color: var(--dees-color-text-secondary); }
    .collapsed .sub.selected { color: var(--dees-color-accent-primary); }
    /* The content column resizes once; only sidebar contents animate during a rail toggle. */
    .maincontainer:not(.collapsed) .viewTab span, .maincontainer:not(.collapsed) .appName, .maincontainer:not(.collapsed) .action span { animation: labelReveal 160ms var(--dees-ease-standard); }
    .collapsed .viewTab > dees-icon:not(.chevron), .collapsed .action > dees-icon { animation: railSettle 160ms var(--dees-ease-standard); }
    @keyframes labelReveal { from { opacity: 0; transform: translateX(-4px); } to { opacity: 1; transform: translateX(0); } }
    @keyframes railSettle { from { transform: translateX(-4px); } to { transform: translateX(0); } }
    @media (hover: hover) { button:hover:not(.selected) { background: var(--dees-color-hover); } .action:hover { color: var(--dees-color-text-error); } }
    @media (pointer: coarse) { .viewTab, .viewTab.sub, .action, .collapseToggle, .messageBanner-dismiss { min-height: 44px; } .maincontainer { grid-template-rows: minmax(0, 1fr) 44px; } }
    @media (prefers-reduced-motion: reduce) { .maincontainer, .viewTab, .action, .chevron, .subViews, .subViews-inner { transition: none; } .maincontainer * { animation: none !important; } }
  `];

  private get rail(): boolean { return this.collapsed || this.compact; }
  private icon(name: string): string { return name.includes(':') ? name : `lucide:${name}`; }
  private isGroupActive(view: IView): boolean { return view === this.selectedView || !!view.subViews?.includes(this.selectedView); }

  private renderView(view: IView, sub = false): TemplateResult {
    const selected = this.selectedView === view;
    const expanded = this.isGroupActive(view);
    return html`<button type="button" class="viewTab ${sub ? 'sub' : ''} ${selected ? 'selected' : ''} ${expanded && !selected ? 'groupActive' : ''}"
      aria-label=${view.name} aria-current=${selected ? 'page' : 'false'}
      @keydown=${this.handleNavigationKey} @click=${() => this.loadView(view)}>
      <dees-icon .icon=${this.icon(view.iconName || (sub ? 'dot' : 'file'))} iconSize="17"></dees-icon><span>${view.name}</span>
      ${view.subViews?.length ? html`<dees-icon class="chevron" icon="lucide:chevronDown" iconSize="12"></dees-icon>` : ''}
    </button><dees-speechbubble hint .disabled=${!this.rail} .text=${view.name}></dees-speechbubble>`;
  }

  public render(): TemplateResult {
    const parent = this.viewTabs.find(view => view.subViews?.includes(this.selectedView));
    return html`<div class="maincontainer ${this.rail ? 'collapsed' : ''}">
      <aside class="appbar" aria-label=${this.name}>
        <div class="sidebar-header"><dees-icon icon="lucide:layers" iconSize="24"></dees-icon><div class="appName">${this.name}</div>
          <button type="button" class="collapseToggle" aria-label=${this.compact ? 'Compact navigation' : this.rail ? 'Expand navigation' : 'Collapse navigation'}
            ?disabled=${this.compact} @click=${() => { this.collapsed = !this.collapsed; }}>
            <dees-icon .icon=${this.rail ? 'lucide:panelLeftOpen' : 'lucide:panelLeftClose'} iconSize="17"></dees-icon>
          </button>
        </div>
        <nav class="viewTabs-container" aria-label="App navigation"><div class="section-label">Workspace</div><div class="viewTabs">
          ${this.viewTabs.map(view => html`<div class="viewGroup">${this.renderView(view)}${view.subViews?.length ? html`<div class="subViews ${this.isGroupActive(view) ? 'expanded' : ''}" ?inert=${!this.isGroupActive(view)}>
            <div class="subViews-inner">${view.subViews.map(sub => this.renderView(sub, true))}</div></div>` : ''}</div>`)}
        </div></nav>
        <div class="appActions"><button type="button" class="action" aria-label="Sign out" @click=${() => this.dispatchEvent(new CustomEvent('logout', { bubbles: true, composed: true }))}>
          <dees-icon icon="lucide:logOut" iconSize="17"></dees-icon><span>Sign out</span>
        </button><dees-speechbubble hint .disabled=${!this.rail} text="Sign out"></dees-speechbubble></div>
      </aside>
      <section class="workspace">
        <header class="workspaceHeader">${parent ? html`<span class="parent">${parent.name}</span><dees-icon icon="lucide:chevronRight" iconSize="12"></dees-icon>` : ''}<span>${this.selectedView?.name || this.name}</span></header>
        <div class="messageBannerArea" aria-live="polite">
          ${this._activeMessages.map(message => html`<div class="messageBanner messageBanner-${message.type}" data-message-id=${message.id}>
            <span class="messageIcon"><dees-icon .icon=${this.getMessageIcon(message)} iconSize="16"></dees-icon></span>
            <span class="messageBanner-text">${message.message}</span>
            ${message.actions?.length ? html`<div class="messageBanner-actions">${message.actions.map(action => html`<dees-button size="sm" type="outline" .text=${action.name} .icon=${action.iconName ? this.icon(action.iconName) : ''} @clicked=${() => action.action()}></dees-button>`)}</div>` : ''}
            ${message.dismissible !== false ? html`<button type="button" class="messageBanner-dismiss" aria-label=${`Dismiss: ${message.message}`} @click=${() => this.removeMessage(message.id)}><dees-icon icon="lucide:x" iconSize="14"></dees-icon></button>` : ''}
          </div>`)}
        </div>
        <div class="contentArea"><div class="appcontent" ?inert=${this.terminalOpen}></div><div class="terminalLayer" ?hidden=${!this.terminalOpen}></div></div>
      </section>
      <footer class="controlbar"><span class="control status-connected"><dees-icon icon="lucide:wifi" iconSize="12"></dees-icon>Connected</span>
        <button type="button" class="control status-terminal" aria-pressed=${String(this.terminalOpen)} @click=${this.launchTerminal}><dees-icon icon="lucide:terminal" iconSize="13"></dees-icon>Terminal</button>
      </footer>
    </div>`;
  }

  private handleNavigationKey(event: KeyboardEvent): void {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const buttons = [...this.shadowRoot!.querySelectorAll<HTMLButtonElement>('.viewTab')].filter(button => !button.closest('[inert]'));
    const index = buttons.indexOf(event.currentTarget as HTMLButtonElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
    buttons[next]?.focus();
  }

  public willUpdate(changes: Map<string, unknown>): void {
    if (changes.has('globalMessages')) {
      const previousIds = new Set(((changes.get('globalMessages') as IGlobalMessage[]) || []).map(message => message.id));
      const incoming = new Map(this.globalMessages.map(message => [message.id, message]));
      this._activeMessages = [...this._activeMessages.filter(message => !previousIds.has(message.id) && !incoming.has(message.id)), ...incoming.values()];
    }
  }

  public updated(changes: Map<string, unknown>): void {
    super.updated(changes);
    if (changes.has('viewTabs')) {
      const views = this.viewTabs.flatMap(view => [view, ...(view.subViews || [])]);
      if (!views.includes(this.selectedView)) {
        this.currentView?.remove();
        this.currentView = undefined;
        this.selectedView = undefined!;
      }
      if (!this.currentView && this.viewTabs.length) void this.loadView(this.selectedView || this.viewTabs[0]);
    }
  }

  private getMessageIcon(message: IGlobalMessage): string {
    const icons: Record<TGlobalMessageType, string> = { info: 'info', success: 'circleCheck', warning: 'triangleAlert', error: 'circleX' };
    return this.icon(message.icon || icons[message.type]);
  }
  public addMessage(message: Omit<IGlobalMessage, 'id'> & { id?: string }): string {
    const id = message.id || `msg-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    const next = { dismissible: true, ...message, id };
    this._activeMessages = this._activeMessages.some(entry => entry.id === id)
      ? this._activeMessages.map(entry => entry.id === id ? next : entry) : [...this._activeMessages, next];
    return id;
  }
  public removeMessage(id: string): void {
    if (!this._activeMessages.some(message => message.id === id)) return;
    this._activeMessages = this._activeMessages.filter(message => message.id !== id);
    this.dispatchEvent(new CustomEvent('message-dismiss', { detail: { id }, bubbles: true, composed: true }));
  }
  public clearMessages(): void { this._activeMessages = []; }

  public async launchTerminal(): Promise<DeesWorkspaceTerminal | undefined> {
    if (this.terminalOpen) { this.closeTerminal(); return; }
    this.terminalOpen = true;
    const request = ++this.terminalRequest;
    try {
      const { DeesWorkspaceTerminal } = await import('../../00group-workspace/dees-workspace-terminal/dees-workspace-terminal.js');
      await this.updateComplete;
      if (!this.isConnected || request !== this.terminalRequest) return;
      const terminal = new DeesWorkspaceTerminal();
      terminal.executionEnvironment = this.executionEnvironment;
      terminal.setupCommand = this.terminalSetupCommand;
      this.currentTerminal = terminal;
      terminal.addEventListener('close', () => { if (terminal === this.currentTerminal) this.closeTerminal(); });
      this.shadowRoot!.querySelector('.terminalLayer')!.appendChild(terminal);
      return terminal;
    } catch (error) {
      if (request === this.terminalRequest) this.closeTerminal();
      throw error;
    }
  }
  private closeTerminal(): void {
    ++this.terminalRequest;
    this.currentTerminal?.remove();
    this.currentTerminal = null;
    this.terminalOpen = false;
  }
  public async disconnectedCallback(): Promise<void> { this.closeTerminal(); await super.disconnectedCallback(); }

  public async loadView(view: IView): Promise<void> {
    if (!view.element && view.subViews?.length) {
      const child = view.subViews.find(candidate => candidate.element);
      if (child) await this.loadView(child);
      return;
    }
    if (!view.element || (view === this.selectedView && this.currentView)) return;
    const content = this.shadowRoot?.querySelector('.appcontent');
    if (!content) return;
    const element = new view.element();
    this.closeTerminal();
    this.currentView?.remove();
    content.appendChild(element);
    content.scrollTop = 0;
    this.currentView = element;
    this.selectedView = view;
    this.dispatchEvent(new CustomEvent('view-select', { detail: { view }, bubbles: true, composed: true }));
  }
}
